<script lang="ts" setup>
import { useSchemaOrg, defineWebPage } from '@unhead/schema-org/vue';
import { useSdk } from '~/sdk';
import { getCmsImage } from '@/composables/useCms';
import { useBlogPaths } from '@/composables/useBlogPaths';

const { locale, t } = useI18n();
const route = useRoute();
const { getArticlePath, getAuthorPath, getIndexPath, getTermPath } = useBlogPaths();

const { data, error } = await useAsyncData('blog-' + locale.value + '-' + route.params.slug, async () => {
  return useSdk().cms.getContent({
    entry: {
      section: 'blog',
      slug: route.params.slug,
    },
  });
});

if (error.value) {
  throw createError({
    statusCode: 502,
    statusMessage: 'CMS request failed',
    message: 'Unable to load the article',
    cause: error.value,
    fatal: true,
  });
}

if (!data.value) {
  throw createError({
    statusCode: 404,
    message: t('Page not found'),
  });
}

const canonicalCategory = data.value?.category?.[0]?.slug;

if (!canonicalCategory) {
  throw createError({
    statusCode: 404,
    message: t('Page not found'),
  });
}

if (canonicalCategory && canonicalCategory !== route.params.category) {
  await navigateTo(getArticlePath(data.value), { redirectCode: 301 });
}

useSeoMeta({
  title: data.value?.seo?.title,
  description: data.value?.seo?.description,
  ogTitle: data.value?.seo?.title,
  ogDescription: data.value?.seo?.description,
});

useSchemaOrg([
  defineWebPage({
    title: data.value?.seo?.title,
    description: data.value?.seo?.description,
  }),
]);

const formatDate = (date: string) => new Date(date).toLocaleDateString(locale.value, { day: 'numeric', month: 'long', year: 'numeric' });

const navigationKey = data.value.id ?? route.params.slug;
const { data: navigation, error: navigationError } = await useAsyncData(`blog-navigation-${locale.value}-${navigationKey}`, async () => {
  if (!data.value?.id || !data.value.postDate) {
    return { previous: null, next: null };
  }

  return useSdk().cms.getNewsNavigation({
    id: data.value.id,
    postDate: data.value.postDate,
  });
});

if (navigationError.value) {
  throw createError({
    statusCode: 502,
    statusMessage: 'CMS request failed',
    message: 'Unable to load the article navigation',
    cause: navigationError.value,
    fatal: true,
  });
}

const prevArticle = computed(() => navigation.value?.previous ?? null);
const nextArticle = computed(() => navigation.value?.next ?? null);

const postDate = computed(() => data.value?.postDate ?? null);

</script>

<template>
  <div v-if="data" class="py-16">
    <VContainer>
      <!-- Article content: narrower centered column, text stays left-aligned -->
      <div class="max-w-6xl mx-auto">
        <!-- Breadcrumbs -->
        <Breadcrumbs :breadcrumbs="[
          { title: t('Home'), url: `/${locale}` },
          { title: 'Blog', url: getIndexPath() },
          { title: data.title, url: '' },
        ]" class="mb-6" />
        <!-- Article header -->
        <div class="mb-8">
          <h1 class="text-3xl font-bold mb-4">{{ data.title }}</h1>
          <div v-if="data.shortDescription?.html" class="prose max-w-none text-lg mb-4" v-html="data.shortDescription.html"></div>
          <div class="flex items-center gap-3 text-sm text-gray-400">
            <span v-if="data.authorBlog?.[0]?.title">{{ t('blog.Author') }}:
              <NuxtLink v-if="data.authorBlog[0].uri" :to="getAuthorPath(data.authorBlog[0])" class="font-medium text-black hover:underline">{{ data.authorBlog[0].title }}</NuxtLink>
              <span v-else class="font-medium text-black">{{ data.authorBlog[0].title }}</span>
            </span>
            <span v-if="postDate">{{ formatDate(postDate) }}</span>
          </div>
        </div>

        <!-- Hero image -->
        <div v-if="data.image?.[0]?.url" class="mb-10">
          <NuxtImg :src="getCmsImage(data.image)" :alt="data.image[0].title" class="w-full rounded object-cover max-h-[500px]" />
        </div>

        <!-- Body -->
        <BlockRenderer v-if="data.bodyBlog?.chunks?.length" :components="data.bodyBlog.chunks" class="space-y-8" />

        <!-- Tags -->
        <div v-if="data.tags?.length" class="flex flex-wrap gap-2 py-10">
          <NuxtLink v-for="tag in data.tags" :key="tag.slug" :to="getTermPath(tag)" class="inline-block bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
            {{ tag.title }}
          </NuxtLink>
        </div>

        <!-- Prev / next navigation -->
        <div v-if="prevArticle || nextArticle" class="pt-8 mt-8 grid grid-cols-2 gap-8">
          <div v-if="prevArticle">
            <NuxtLink :to="getArticlePath(prevArticle)" class="inline-flex items-start gap-3 group">
              <span class="text-primary text-4xl leading-none mt-1">&lsaquo;</span>
              <div>
                <p class="text-sm text-gray-400 mb-1">{{ t('blog.Previous article') }}</p>
                <p class="font-semibold text-primary group-hover:underline">{{ prevArticle.title }}</p>
              </div>
            </NuxtLink>
          </div>
          <div v-else></div>

          <div v-if="nextArticle" class="flex justify-end">
            <NuxtLink :to="getArticlePath(nextArticle)" class="inline-flex items-start gap-3 text-right group">
              <div>
                <p class="text-sm text-gray-400 mb-1">{{ t('blog.Next article') }}</p>
                <p class="font-semibold text-primary group-hover:underline">{{ nextArticle.title }}</p>
              </div>
              <span class="text-primary text-4xl leading-none mt-1">&rsaquo;</span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </VContainer>

  </div>
</template>
